import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Chip,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { KubernetesActionType, KubernetesAction } from './types';
import {
  parseCronExpression,
  validateCronExpression,
  formatCronParts,
  getCronTemplates,
} from '../cronUtils';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  cronPreview: {
    backgroundColor: '#263238',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    border: '1px solid #326CE5',
  },
  cronParts: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  templateButton: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  validationError: {
    marginTop: theme.spacing(1),
  },
}));

interface KubernetesScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (scheduleKey: string, scheduleValue: string) => void;
  action: KubernetesActionType | null;
  existingSchedules: Array<{ key: string; value: string }>;
  isLoading: boolean;
  initialScheduleType?: 'start' | 'stop';
  selectedSchedule?: { key: string; value: string } | null;
}

export const KubernetesScheduleDialog: React.FC<
  KubernetesScheduleDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  action,
  existingSchedules,
  isLoading,
  initialScheduleType = 'start',
  selectedSchedule,
}) => {
  const classes = useStyles();
  const [scheduleType, setScheduleType] = useState<'start' | 'stop'>(
    initialScheduleType,
  );
  const [scheduleKey, setScheduleKey] = useState('');
  const [cronExpression, setCronExpression] = useState('');
  const [validation, setValidation] = useState<{
    valid: boolean;
    error?: string;
  }>({ valid: true });
  const [duplicateError, setDuplicateError] = useState<string>('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setScheduleType(initialScheduleType);

      if (
        action === KubernetesAction.UPDATE_SCHEDULER ||
        action === KubernetesAction.DELETE_SCHEDULER
      ) {
        if (selectedSchedule) {
          setScheduleKey(selectedSchedule.key.replace('cloudstart/', ''));
          setCronExpression(selectedSchedule.value);
        }
      } else if (action === KubernetesAction.ADD_SCHEDULER) {
        // Auto-generate key for add
        const nextKey = getNextScheduleKey(existingSchedules, scheduleType);
        setScheduleKey(nextKey.replace('cloudstart/', ''));
        setCronExpression('');
      }
    }
  }, [
    open,
    action,
    initialScheduleType,
    existingSchedules,
    scheduleType,
    selectedSchedule,
  ]);

  // Validate cron expression on change
  useEffect(() => {
    if (cronExpression) {
      const validationResult = validateCronExpression(cronExpression);
      setValidation(validationResult);

      // Check for duplicate cron expression in the same type
      if (validationResult.valid && action === KubernetesAction.ADD_SCHEDULER) {
        const duplicateSchedule = existingSchedules.find(
          s =>
            s.value === cronExpression &&
            s.key.startsWith(`cloudstart/${scheduleType}`),
        );

        if (duplicateSchedule) {
          setDuplicateError(
            `This schedule already exists as "${duplicateSchedule.key.replace(
              'cloudstart/',
              '',
            )}"`,
          );
        } else {
          setDuplicateError('');
        }
      } else if (
        validationResult.valid &&
        action === KubernetesAction.UPDATE_SCHEDULER
      ) {
        // For update, check if the new cron expression matches another schedule (not the current one)
        const duplicateSchedule = existingSchedules.find(
          s =>
            s.value === cronExpression &&
            s.key.startsWith(`cloudstart/${scheduleType}`) &&
            s.key !== selectedSchedule?.key,
        );

        if (duplicateSchedule) {
          setDuplicateError(
            `This schedule already exists as "${duplicateSchedule.key.replace(
              'cloudstart/',
              '',
            )}"`,
          );
        } else {
          setDuplicateError('');
        }
      } else {
        setDuplicateError('');
      }
    } else {
      setValidation({ valid: true });
      setDuplicateError('');
    }
  }, [
    cronExpression,
    existingSchedules,
    scheduleType,
    action,
    selectedSchedule,
  ]);

  const getNextScheduleKey = (
    schedules: Array<{ key: string; value: string }>,
    type: 'start' | 'stop',
  ): string => {
    const prefix = `cloudstart/${type}`;
    const existingKeys = schedules
      .filter(s => s.key.startsWith(prefix))
      .map(s => s.key);

    if (!existingKeys.includes(prefix)) {
      return prefix;
    }

    let counter = 1;
    while (existingKeys.includes(`${prefix}-${counter}`)) {
      counter++;
    }
    return `${prefix}-${counter}`;
  };

  const handleConfirm = () => {
    if (!cronExpression || !validation.valid || duplicateError) {
      return;
    }
    onConfirm(scheduleKey, cronExpression);
  };

  const handleTemplateSelect = (template: string) => {
    setCronExpression(template);
  };

  const getDialogTitle = () => {
    if (action === KubernetesAction.ADD_SCHEDULER) {
      return `Add ${scheduleType === 'start' ? 'Start' : 'Stop'} Schedule`;
    }
    if (action === KubernetesAction.UPDATE_SCHEDULER) {
      return `Update ${scheduleType === 'start' ? 'Start' : 'Stop'} Schedule`;
    }
    if (action === KubernetesAction.DELETE_SCHEDULER) {
      return `Delete ${scheduleType === 'start' ? 'Start' : 'Stop'} Schedule`;
    }
    return 'Schedule Configuration';
  };

  const isDeleteAction = action === KubernetesAction.DELETE_SCHEDULER;
  const cronParts = cronExpression ? formatCronParts(cronExpression) : null;
  const cronDescription = cronExpression
    ? parseCronExpression(cronExpression)
    : '';

  const templates = getCronTemplates();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {isDeleteAction ? (
          <Box>
            <Alert severity="warning">
              Are you sure you want to delete this schedule?
            </Alert>
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Schedule Key:
              </Typography>
              <Typography variant="body1" style={{ fontFamily: 'monospace' }}>
                {scheduleKey}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                Cron Expression:
              </Typography>
              <Typography variant="body1" style={{ fontFamily: 'monospace' }}>
                {cronExpression}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                {cronDescription}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <FormControl className={classes.formControl}>
              <TextField
                label="Schedule Key"
                value={scheduleKey}
                disabled
                variant="outlined"
                fullWidth
                helperText={
                  action === KubernetesAction.UPDATE_SCHEDULER
                    ? 'Selected schedule'
                    : 'Auto-generated based on existing schedules'
                }
              />
            </FormControl>

            <FormControl className={classes.formControl}>
              <TextField
                label="Cron Expression"
                value={cronExpression}
                onChange={e => setCronExpression(e.target.value)}
                variant="outlined"
                fullWidth
                placeholder="0 9 * * 1-5"
                error={!validation.valid}
                helperText={
                  validation.error ||
                  'Enter a cron expression (minute hour day month weekday)'
                }
              />
            </FormControl>

            {validation.error && (
              <Alert severity="error" className={classes.validationError}>
                {validation.error}
              </Alert>
            )}

            {duplicateError && (
              <Alert severity="error" className={classes.validationError}>
                {duplicateError}
              </Alert>
            )}

            {validation.valid && !duplicateError && cronExpression && (
              <Box className={classes.cronPreview}>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccessTimeIcon
                    style={{ marginRight: 8, color: '#64B5F6' }}
                  />
                  <Typography
                    variant="body2"
                    style={{ color: '#ffffff', fontWeight: 'bold' }}
                  >
                    Preview:
                  </Typography>
                </Box>
                <Typography variant="body1" style={{ color: '#00E676' }}>
                  {cronDescription}
                </Typography>
                {cronParts && (
                  <Box className={classes.cronParts}>
                    <Chip
                      size="small"
                      label={`Minute: ${cronParts.minute}`}
                      style={{
                        backgroundColor: '#1565C0',
                        color: '#ffffff',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Hour: ${cronParts.hour}`}
                      style={{
                        backgroundColor: '#6A1B9A',
                        color: '#ffffff',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Day: ${cronParts.dayOfMonth}`}
                      style={{
                        backgroundColor: '#EF6C00',
                        color: '#ffffff',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Month: ${cronParts.month}`}
                      style={{
                        backgroundColor: '#00695C',
                        color: '#ffffff',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`Weekday: ${cronParts.dayOfWeek}`}
                      style={{
                        backgroundColor: '#AD1457',
                        color: '#ffffff',
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            <Alert severity="info" style={{ marginTop: 16 }}>
              <Typography variant="body2">
                <strong>Cron Expression Format:</strong> minute hour day month
                weekday
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                Example: <code>0 9 * * 1-5</code> = Every weekday at 9:00 AM
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                Need help? Visit{' '}
                <a
                  href="https://crontab.guru/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#64B5F6', textDecoration: 'underline' }}
                >
                  crontab.guru
                </a>{' '}
                for interactive examples and explanations.
              </Typography>
            </Alert>

            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Quick templates:
              </Typography>
              <Grid container spacing={1}>
                {templates.map(template => (
                  <Grid item key={template.label}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleTemplateSelect(template.value)}
                      className={classes.templateButton}
                      style={{ textTransform: 'none' }}
                    >
                      {template.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={isDeleteAction ? 'secondary' : 'primary'}
          variant="contained"
          disabled={
            isLoading ||
            (!isDeleteAction &&
              (!cronExpression || !validation.valid || !!duplicateError))
          }
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : isDeleteAction ? (
            'Delete'
          ) : action === KubernetesAction.UPDATE_SCHEDULER ? (
            'Update'
          ) : (
            'Add'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
